import React, { useState, useEffect } from "react";
import "./Managements.css";
import { useNavigate } from "react-router-dom";
import AddUserModal from "../../AddUserModal/AddUserModal";
import ShadcnActionDropdown from "../../common/ShadcnActionDropdown/ShadcnActionDropdown";
import PagesCMS from "../../PagesCMS/PagesCMS";
import { getUsers, getVendors, toggleUserStatus, toggleVendorStatus, verifyUser, verifyVendor, checkBackendHealth, testAuthentication } from "../../../services/userManagement";
import LoadingSpinner from "../../common/LoadingSpinner/LoadingSpinner";

const Managements = () => {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState("Users");
  const [activeTab, setActiveTab] = useState("management");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Separate states for users and vendors
  const [users, setUsers] = useState([]);
  const [vendors, setVendors] = useState([]);

  // Fetch data on component mount
  useEffect(() => {
    const initializeData = async () => {
      // First test authentication
      const isAuthenticated = await testAuthentication();
      if (!isAuthenticated) {
        setError("Authentication failed. Please check your login status.");
        return;
      }

      // Then check backend health
      const isBackendHealthy = await checkBackendHealth();
      if (!isBackendHealthy) {
        setError("Backend server is not accessible. Please ensure the backend is running on port 3000.");
        return;
      }

      // Then fetch data
      await fetchData();
    };

    initializeData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [usersData, vendorsData] = await Promise.allSettled([
        getUsers(),
        getVendors()
      ]);

      // Handle users data
      let transformedUsers = [];
      if (usersData.status === 'fulfilled') {
        transformedUsers = (usersData.value.data || []).map(user => ({
          _id: user.id ? user.id.toString() : user.getId ? user.getId().toString() : null,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          role: user.roles && user.roles.length > 0 ? user.roles[0] : 'BUYER',
          phoneNumber: user.phoneNumber || 'N/A',
          verified: user.verified,
          isBlocked: user.isBlocked || false,
          profileImage: user.profileImage || null,
        }));
      }

      // Handle vendors data
      let transformedVendors = [];
      if (vendorsData.status === 'fulfilled') {
        console.log("Raw vendors data:", vendorsData.value.data);
        transformedVendors = (vendorsData.value.data || []).map(vendor => {
          console.log("Processing vendor:", vendor);
          const vendorId = vendor.id ? vendor.id.toString() : vendor.getId ? vendor.getId().toString() : null;
          console.log("Vendor ID:", vendorId);
          return {
            _id: vendorId,
            firstName: vendor.firstName,
            lastName: vendor.lastName,
            email: vendor.email,
            role: vendor.roles && vendor.roles.length > 0 ? vendor.roles[0] : 'VENDOR',
            phoneNumber: vendor.phoneNumber || 'N/A',
            verified: vendor.verified,
            isBlocked: vendor.disabled || false,
            profileImage: vendor.profileImage || null,
            businessName: vendor.businessName || 'N/A',
          };
        });
      }

      // If both failed, show error and don't set mock data
      if (usersData.status === 'rejected' && vendorsData.status === 'rejected') {
        setError("Failed to fetch data. Please check your connection and try again.");
        transformedUsers = [];
        transformedVendors = [];
      }

      setUsers(transformedUsers);
      setVendors(transformedVendors);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = (newData) => {
    if (selectedType === "Users") {
      setUsers([
        ...users,
        {
          ...newData,
          _id: `user-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        },
      ]);
    } else {
      setVendors([
        ...vendors,
        {
          ...newData,
          _id: `vendor-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        },
      ]);
    }
    setIsModalOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setIsDropdownOpen(false);
  };

  // Handle block/unblock user or vendor
  const handleToggleStatus = async (itemId, isBlocked) => {
    try {
      if (selectedType === "Users") {
        await toggleUserStatus(itemId, isBlocked);
        setUsers(users.map(user =>
          user._id === itemId ? { ...user, isBlocked } : user
        ));
        alert(`User ${isBlocked ? 'blocked' : 'unblocked'} successfully!`);
      } else {
        await toggleVendorStatus(itemId, isBlocked);
        setVendors(vendors.map(vendor =>
          vendor._id === itemId ? { ...vendor, isBlocked } : vendor
        ));
        alert(`Vendor ${isBlocked ? 'blocked' : 'unblocked'} successfully!`);
      }
    } catch (error) {
      console.error("Error toggling status:", error);
      setError("Failed to update status. Please try again.");

      // Revert the change in UI if API call failed
      if (selectedType === "Users") {
        setUsers(users.map(user =>
          user._id === itemId ? { ...user, isBlocked: !isBlocked } : user
        ));
      } else {
        setVendors(vendors.map(vendor =>
          vendor._id === itemId ? { ...vendor, isBlocked: !isBlocked } : vendor
        ));
      }
    }
  };

  // Handle verify user or vendor
  const handleVerify = async (itemId) => {
    try {
      console.log("Attempting to verify:", selectedType, "with ID:", itemId);

      if (selectedType === "Users") {
        const result = await verifyUser(itemId);
        console.log("Verify user result:", result);
        setUsers(users.map(user =>
          user._id === itemId ? { ...user, verified: true } : user
        ));
        alert("User verified successfully!");
      } else {
        const result = await verifyVendor(itemId);
        console.log("Verify vendor result:", result);
        setVendors(vendors.map(vendor =>
          vendor._id === itemId ? { ...vendor, verified: true } : vendor
        ));
        alert("Vendor verified successfully!");
      }
    } catch (error) {
      console.error("Error verifying:", error);
      console.error("Error details:", error.response?.data);
      setError(`Failed to verify ${selectedType.slice(0, -1).toLowerCase()}. Please try again. Error: ${error.response?.data?.message || error.message}`);
    }
  };

  // Get current data based on selected type
  const currentData = selectedType === "Users" ? users : vendors;

  return (
    <div className="managements-container">
      <div className="managements-tabs">
        <button
          className={`tab-button ${activeTab === 'management' ? 'active' : ''}`}
          onClick={() => setActiveTab('management')}
        >
          User & Vendor Management
        </button>
        <button
          className={`tab-button ${activeTab === 'cms' ? 'active' : ''}`}
          onClick={() => setActiveTab('cms')}
        >
          Pages CMS
        </button>
      </div>

      {activeTab === 'management' && (
        <>
          <div className="managements-header">
            <div className="header-left">
              <div className="dropdown-container">
                <button className="dropdown-button" onClick={toggleDropdown}>
                  {selectedType}
                </button>
                {isDropdownOpen && (
                  <div className="dropdown-content">
                    <button onClick={() => handleTypeSelect("Users")}>Users</button>
                    <button onClick={() => handleTypeSelect("Vendors")}>
                      Vendors
                    </button>
                  </div>
                )}
              </div>
            </div>
            <button className="add-button" onClick={() => setIsModalOpen(true)}>
              + Add
            </button>
          </div>

          {isModalOpen && (
            <AddUserModal
              onSave={handleAdd}
              onClose={() => setIsModalOpen(false)}
              type={selectedType}
            />
          )}

          {error && (
            <div className="error-message">
              <p>{error}</p>
              <p style={{ fontSize: '0.9rem', color: '#6c757d', marginTop: '0.5rem' }}>
                This might be due to backend connectivity issues. Please ensure the backend server is running.
              </p>
              <button onClick={fetchData} className="retry-button">
                Retry
              </button>
            </div>
          )}

          <div className="users-count">
            {selectedType}: {currentData.length}
          </div>

          {loading ? (
            <LoadingSpinner message={`Loading ${selectedType.toLowerCase()}...`} />
          ) : (
            <div className="table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Profile</th>
                    <th>First Name</th>
                    <th>Last Name</th>
                    {selectedType === "Vendors" && <th>Business Name</th>}
                    <th>Role</th>
                    <th>Phone Number</th>
                    <th>Verified</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.map((item) => (
                    <tr key={item._id}>
                      <td>
                        <div className="profile-image">
                          {item.profileImage ? (
                            <img
                              src={item.profileImage}
                              alt={`${item.firstName}'s profile`}
                            />
                          ) : (
                            <div className="profile-placeholder"></div>
                          )}
                        </div>
                      </td>
                      <td>{item.firstName}</td>
                      <td>{item.lastName}</td>
                      {selectedType === "Vendors" && <td>{item.businessName}</td>}
                      <td>{item.role}</td>
                      <td>{item.phoneNumber}</td>
                      <td>
                        <span
                          className={`verification-badge ${item.verified ? "verified" : "not-verified"
                            }`}
                        >
                          {item.verified ? "Verified" : "Not Verified"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`status-badge ${item.isBlocked ? "blocked" : "active"
                            }`}
                        >
                          {item.isBlocked ? "Blocked" : "Active"}
                        </span>
                      </td>
                      <td>
                        <ShadcnActionDropdown
                          size="small"
                          variant="default"
                          actions={[
                            {
                              label: item.isBlocked ? 'Unblock' : 'Block',
                              icon: 'settings',
                              onClick: () => handleToggleStatus(item._id, !item.isBlocked),
                              tooltip: item.isBlocked ? 'Unblock this user' : 'Block this user'
                            },
                            ...(!item.verified ? [{
                              label: 'Verify',
                              icon: 'settings',
                              onClick: () => handleVerify(item._id),
                              tooltip: 'Verify this user'
                            }] : []),
                            {
                              label: 'View Details',
                              icon: 'view',
                              onClick: () => {
                                console.log("Navigating to details for item:", item);
                                console.log("Item ID:", item._id);
                                navigate(
                                  selectedType === "Users"
                                    ? `/user-details/${item._id}`
                                    : `/vendor-details/${item._id}`
                                );
                              },
                              tooltip: 'View user details'
                            }
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === 'cms' && (
        <PagesCMS />
      )}
    </div>
  );
};

export default Managements;
